import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Shield,
  Languages,
  BarChart3,
  Loader2,
  ChevronDown,
  ChevronUp,
  Mail,
  FileText,
  Bot,
  MessageSquare,
  Layout,
  BookOpen,
  Info,
  Check,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { adminFetch } from '../lib/api';

// The server-side translation-status API returns full coverage for ALL 7 criteria

interface CriteriaItem {
  name: string;
  label: string;
  translated: number;
  total: number;
  pct: number;
}

interface LanguageStatus {
  code: string;
  label: string;
  flag: string;
  enabled: boolean;
  rtl: boolean;
  criteria: CriteriaItem[];
  overallPct: number;
  allComplete: boolean;
  missingLocaleKeys?: string[];
  germanEmailSlugs?: string[];
}

interface TranslationStatusResponse {
  languages: LanguageStatus[];
  totals: { questions: number; emailTemplates: number; localeKeys: number };
}

const GATE_THRESHOLD = 100;

const CRITERIA_ICONS: Record<string, React.ReactNode> = {
  locale_keys: <Layout size={14} />,
  questions: <BookOpen size={14} />,
  legal_texts: <FileText size={14} />,
  email_templates: <Mail size={14} />,
  email_bodies: <MessageSquare size={14} />,
  email_footers: <Mail size={14} />,
  ai_coach: <Bot size={14} />,
};

const CRITERIA_DESC: Record<string, string> = {
  locale_keys: 'Locale JSON Keys',
  questions: 'Prüfungsfragen',
  legal_texts: 'Datenschutz, AGB, Cookies, Impressum',
  email_templates: 'Subject & Preheader',
  email_bodies: 'body_html übersetzt',
  email_footers: 'Footer-Strings',
  ai_coach: 'LANG_NAMES + Prompts',
};

export const LanguagesPage: React.FC = () => {
  const [data, setData] = useState<LanguageStatus[]>([]);
  const [totals, setTotals] = useState({ questions: 0, emailTemplates: 0, localeKeys: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [expandedLang, setExpandedLang] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    text: string;
    type: 'success' | 'warning' | 'error';
  } | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const showToast = (text: string, type: 'success' | 'warning' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await adminFetch('/api/translation-status');

      if (!resp.ok) {
        const errText = await resp.text().catch(() => 'Unknown error');
        throw new Error(`API Error ${resp.status}: ${errText.slice(0, 200)}`);
      }

      const result: TranslationStatusResponse = await resp.json();
      setData(result.languages);
      setTotals(result.totals);
    } catch (err: any) {
      console.error('Translation status error:', err);
      setApiError(err.message || 'Fehler beim Laden');

      // Fallback: try Supabase view
      const { data: fallback } = await adminDb.from('language_translation_status').select('*');
      if (fallback) {
        setData(
          fallback.map((lang) => ({
            code: lang.code,
            label: lang.label,
            flag: lang.flag,
            enabled: lang.enabled,
            rtl: lang.rtl,
            overallPct: Math.round(
              (lang.questions_pct + lang.email_templates_pct + lang.email_bodies_pct) / 3
            ),
            allComplete:
              lang.questions_pct >= 100 &&
              lang.email_templates_pct >= 100 &&
              lang.email_bodies_pct >= 100,
            criteria: [
              {
                name: 'locale_keys',
                label: 'UI-Übersetzung',
                translated: 0,
                total: 0,
                pct: lang.code === 'de' ? 100 : -1,
              },
              {
                name: 'questions',
                label: 'Fragen',
                translated: lang.questions_translated,
                total: lang.questions_total,
                pct: lang.questions_pct,
              },
              {
                name: 'legal_texts',
                label: 'Rechtstexte',
                translated: 0,
                total: 1,
                pct: lang.code === 'de' ? 100 : -1,
              },
              {
                name: 'email_templates',
                label: 'Email Templates',
                translated: lang.email_templates_count,
                total: lang.email_templates_de_count,
                pct: lang.email_templates_pct,
              },
              {
                name: 'email_bodies',
                label: 'Email Inhalte',
                translated: lang.email_bodies_translated,
                total: lang.email_bodies_total,
                pct: lang.email_bodies_pct,
              },
              {
                name: 'email_footers',
                label: 'Email Footers',
                translated: 0,
                total: 1,
                pct: lang.code === 'de' ? 100 : -1,
              },
              {
                name: 'ai_coach',
                label: 'KI-Coach',
                translated: 0,
                total: 1,
                pct: lang.code === 'de' ? 100 : -1,
              },
            ],
          }))
        );
        showToast('API nicht erreichbar — Fallback auf DB-View (teilweise Daten)', 'warning');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleLanguage = async (lang: LanguageStatus) => {
    if (lang.code === 'de' && lang.enabled) {
      showToast('Deutsch kann nicht deaktiviert werden (Fallback-Sprache).', 'warning');
      return;
    }

    if (!lang.enabled && !lang.allComplete) {
      showToast('Alle 7 Kriterien müssen zu 100% erfüllt sein.', 'error');
      return;
    }

    setSaving(lang.code);
    try {
      // Get admin email from stored session
      const storedSession = localStorage.getItem('fahryo_admin_session');
      const email = storedSession ? JSON.parse(storedSession)?.email || 'unknown' : 'unknown';

      const { error } = await adminDb
        .from('app_languages')
        .update({ enabled: !lang.enabled, updated_at: new Date().toISOString() })
        .eq('code', lang.code);

      if (error) throw error;

      await adminDb.from('audit_logs').insert({
        admin_email: email,
        action: 'language.toggle',
        resource_type: 'app_language',
        resource_id: lang.code,
        old_value: { enabled: lang.enabled },
        new_value: { enabled: !lang.enabled },
      });

      setData((prev) =>
        prev.map((l) => (l.code === lang.code ? { ...l, enabled: !lang.enabled } : l))
      );
      showToast(
        `${lang.flag} ${lang.label} ${!lang.enabled ? 'aktiviert' : 'deaktiviert'}`,
        'success'
      );
    } catch (err: any) {
      showToast(`Fehler: ${err.message}`, 'error');
    } finally {
      setSaving(null);
    }
  };

  const activeCount = data.filter((l) => l.enabled).length;
  const totalQuestions =
    totals.questions || data[0]?.criteria?.find((c) => c.name === 'questions')?.total || 0;
  const avgPct =
    data.length > 0 ? Math.round(data.reduce((s, l) => s + l.overallPct, 0) / data.length) : 0;
  const fullyTranslated = data.filter((l) => l.allComplete).length;

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <Globe size={24} /> Sprachen & Lokalisierung
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <Globe size={24} /> Sprachen & Lokalisierung
        </h1>
        <button className="btn btn-secondary" onClick={fetchData}>
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            background:
              toast.type === 'success'
                ? '#065f46'
                : toast.type === 'warning'
                  ? '#92400e'
                  : '#991b1b',
            color: 'white',
            padding: '12px 20px',
            borderRadius: 12,
            fontSize: 14,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            fontWeight: 600,
            animation: 'fadeIn 0.3s ease',
            maxWidth: 400,
          }}
        >
          {toast.text}
        </div>
      )}

      {/* API Error Banner */}
      {apiError && (
        <div
          className="card mb-6"
          style={{ background: '#fef2f2', borderLeft: '4px solid #dc2626', color: '#991b1b' }}
        >
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span>
              <strong>API-Fehler:</strong> {apiError}
            </span>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Aktive Sprachen</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>
            {activeCount}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Vollständig übersetzt</span>
          <span
            className="kpi-value"
            style={{ color: fullyTranslated === data.length ? 'var(--success)' : 'var(--warning)' }}
          >
            {fullyTranslated}/{data.length}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Fragen-Pool</span>
          <span className="kpi-value">{totalQuestions}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">⌀ Gesamtabdeckung</span>
          <span
            className="kpi-value"
            style={{
              color:
                avgPct >= 100
                  ? 'var(--success)'
                  : avgPct >= 90
                    ? 'var(--warning)'
                    : 'var(--danger)',
            }}
          >
            {avgPct}%
          </span>
        </div>
      </div>

      {/* Rollout Gate Info */}
      <div
        className="card mb-6"
        style={{ background: 'var(--bg-hover)', borderLeft: '4px solid var(--primary)' }}
      >
        <div className="flex items-center gap-2 text-sm">
          <Shield size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <span>
            <strong>100%-Gate:</strong> Eine Sprache kann erst aktiviert werden, wenn{' '}
            <strong>alle 7 Kriterien</strong> zu 100% erfüllt sind: UI-Locale, Fragen, Rechtstexte,
            Email Templates, Email Inhalte, Email Footers & KI-Coach.
          </span>
        </div>
      </div>

      {/* Language Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {data.map((lang) => {
          const isExpanded = expandedLang === lang.code;
          const isDE = lang.code === 'de';

          return (
            <div
              key={lang.code}
              className="card"
              style={{
                padding: 0,
                overflow: 'hidden',
                borderLeft: `4px solid ${lang.enabled ? 'var(--success)' : lang.allComplete ? '#eab308' : 'var(--border)'}`,
              }}
            >
              {/* Header Row */}
              <div
                onClick={() => setExpandedLang(isExpanded ? null : lang.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  opacity: lang.enabled ? 1 : 0.85,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ fontSize: 32 }}>{lang.flag}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{lang.label}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <code
                        style={{
                          background: 'var(--bg-hover)',
                          padding: '1px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {lang.code.toUpperCase()}
                      </code>
                      {lang.rtl && (
                        <span className="badge badge-info" style={{ fontSize: 10 }}>
                          RTL ←
                        </span>
                      )}
                      {isDE && (
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Fallback</span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* Overall Progress Ring */}
                  <div style={{ position: 'relative', width: 56, height: 56 }}>
                    <svg
                      viewBox="0 0 36 36"
                      style={{ width: 56, height: 56, transform: 'rotate(-90deg)' }}
                    >
                      <circle
                        cx="18"
                        cy="18"
                        r="15"
                        fill="none"
                        stroke="var(--border)"
                        strokeWidth="3"
                      />
                      <circle
                        cx="18"
                        cy="18"
                        r="15"
                        fill="none"
                        stroke={
                          lang.overallPct === 100
                            ? 'var(--success)'
                            : lang.overallPct >= 90
                              ? '#eab308'
                              : 'var(--danger)'
                        }
                        strokeWidth="3"
                        strokeDasharray={`${lang.overallPct * 0.9425} 94.25`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.5s ease' }}
                      />
                    </svg>
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 800,
                        color:
                          lang.overallPct === 100
                            ? 'var(--success)'
                            : lang.overallPct >= 90
                              ? '#eab308'
                              : 'var(--text-muted)',
                      }}
                    >
                      {lang.overallPct}%
                    </span>
                  </div>

                  {/* Status Badge */}
                  {lang.enabled ? (
                    <span
                      className="badge badge-success"
                      style={{ fontSize: 11, minWidth: 80, justifyContent: 'center' }}
                    >
                      <CheckCircle size={12} /> Aktiv
                    </span>
                  ) : lang.allComplete ? (
                    <span
                      className="badge badge-warning"
                      style={{ fontSize: 11, minWidth: 80, justifyContent: 'center' }}
                    >
                      Bereit
                    </span>
                  ) : (
                    <span
                      className="badge badge-neutral"
                      style={{ fontSize: 11, minWidth: 80, justifyContent: 'center' }}
                    >
                      <XCircle size={12} /> Offen
                    </span>
                  )}

                  {/* Toggle */}
                  <button
                    className={`btn btn-sm ${lang.enabled ? 'btn-secondary' : 'btn-primary'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLanguage(lang);
                    }}
                    disabled={
                      saving === lang.code ||
                      (isDE && lang.enabled) ||
                      (!lang.enabled && !lang.allComplete)
                    }
                    style={{
                      minWidth: 120,
                      opacity:
                        (isDE && lang.enabled) || (!lang.enabled && !lang.allComplete) ? 0.4 : 1,
                    }}
                    title={
                      !lang.enabled && !lang.allComplete
                        ? 'Alle 7 Kriterien müssen erfüllt sein'
                        : ''
                    }
                  >
                    {saving === lang.code ? (
                      <>
                        <RefreshCw size={12} className="spin" /> Speichern...
                      </>
                    ) : lang.enabled ? (
                      <>
                        <ToggleRight size={14} /> Deaktivieren
                      </>
                    ) : (
                      <>
                        <ToggleLeft size={14} /> Aktivieren
                      </>
                    )}
                  </button>

                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </div>

              {/* Expandable Detail Panel */}
              {isExpanded && (
                <div
                  style={{
                    borderTop: '1px solid var(--border)',
                    background: 'var(--bg-hover)',
                    padding: '20px 24px',
                    animation: 'fadeIn 0.2s ease',
                  }}
                >
                  <h4
                    style={{
                      margin: '0 0 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                    }}
                  >
                    <BarChart3 size={16} /> Übersetzungsfortschritt — {lang.flag} {lang.label}
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                    {lang.criteria.map((c, i) => {
                      const isFallback = c.pct === -1; // Fallback mode (no API data)
                      const pct = isFallback ? 0 : c.pct;
                      const detail = isFallback
                        ? 'API nicht erreichbar'
                        : `${c.translated}/${c.total}`;

                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 16px',
                            background: 'var(--background)',
                            borderRadius: 8,
                            border: `1px solid ${pct === 100 ? 'var(--success)' : pct > 0 ? '#eab308' : isFallback ? '#6b7280' : 'var(--border)'}`,
                          }}
                        >
                          <div
                            style={{
                              color: pct === 100 ? 'var(--success)' : 'var(--text-muted)',
                              flexShrink: 0,
                            }}
                          >
                            {CRITERIA_ICONS[c.name] || <Layout size={14} />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 2,
                              }}
                            >
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{c.label}</span>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color:
                                    pct === 100
                                      ? 'var(--success)'
                                      : isFallback
                                        ? '#6b7280'
                                        : pct > 0
                                          ? '#eab308'
                                          : 'var(--text-muted)',
                                }}
                              >
                                {detail}
                              </span>
                            </div>
                            <div
                              style={{
                                height: 6,
                                borderRadius: 3,
                                background: 'var(--border)',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: `${Math.min(pct, 100)}%`,
                                  height: '100%',
                                  borderRadius: 3,
                                  background:
                                    pct === 100
                                      ? 'var(--success)'
                                      : pct > 0
                                        ? '#eab308'
                                        : 'transparent',
                                  transition: 'width 0.5s ease',
                                }}
                              />
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                              {CRITERIA_DESC[c.name] || c.name}
                              {isFallback && !isDE && (
                                <span
                                  style={{
                                    color: '#6b7280',
                                    marginLeft: 4,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 3,
                                  }}
                                >
                                  <Info size={10} /> Server-Check benötigt
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            {pct === 100 ? (
                              <CheckCircle size={18} style={{ color: 'var(--success)' }} />
                            ) : isFallback ? (
                              <AlertTriangle size={18} style={{ color: '#6b7280' }} />
                            ) : pct > 0 ? (
                              <AlertTriangle size={18} style={{ color: '#eab308' }} />
                            ) : (
                              <XCircle size={18} style={{ color: '#dc2626' }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Missing locale keys */}
                  {lang.missingLocaleKeys && lang.missingLocaleKeys.length > 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '8px 12px',
                        background: '#fef2f2',
                        borderRadius: 6,
                        fontSize: 11,
                      }}
                    >
                      <strong>Fehlende Locale-Keys:</strong> {lang.missingLocaleKeys.join(', ')}
                    </div>
                  )}

                  {/* German email slugs */}
                  {lang.germanEmailSlugs && lang.germanEmailSlugs.length > 0 && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '8px 12px',
                        background: '#fffbeb',
                        borderRadius: 6,
                        fontSize: 11,
                      }}
                    >
                      <strong>Noch deutsche Email-Bodies:</strong>{' '}
                      {lang.germanEmailSlugs.join(', ')}
                    </div>
                  )}

                  {/* Summary */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 16,
                      padding: '12px 16px',
                      background: lang.allComplete
                        ? 'rgba(22,163,74,0.08)'
                        : 'rgba(234,179,8,0.08)',
                      borderRadius: 8,
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {lang.allComplete ? (
                        <>
                          <CheckCircle size={14} style={{ color: '#16a34a' }} />
                          Alle 7 Kriterien erfüllt — Aktivierung möglich
                        </>
                      ) : (
                        <>
                          <AlertTriangle size={14} style={{ color: '#eab308' }} />
                          {lang.criteria.filter((c) => c.pct < 100 && c.pct !== -1).length} offene
                          Kriterien
                        </>
                      )}
                    </span>
                    <span
                      style={{
                        color: 'var(--text-muted)',
                        fontSize: 11,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                    >
                      {lang.criteria.filter((c) => c.pct >= 100).length}/7 Kriterien{' '}
                      <Check size={11} />
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>
        {`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .grid-cols-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 900px) { .grid-cols-4 { grid-template-columns: repeat(2, 1fr); } }
      `}
      </style>
    </div>
  );
};
